import { Alerts } from "../../../utils/Alerts.js";
import { useRPCContext } from "../../../contexts/RPCContext.js";
import { useStateDebounced } from "../../../hooks/useStateDebounced.js";
import { MAIL_VIEWS } from "../constants.js";
import { useMailStore } from "../stores/MailStore.js";
import { useEffect, useMemo, useState } from "react";
import { Button, ButtonGroup, Icon, Text, Tooltip } from "@therms/atalaya";
import { IoCheckbox, IoMailUnread } from "react-icons/io5";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { pluralize } from "@therms/web-js";
import { MdCheckBoxOutlineBlank, MdChecklist, MdDeleteForever } from "react-icons/md";
import { HiFlag } from "react-icons/hi2";
//#region src/modules/Mail/components/MailMessageListToolbar.tsx
function MailMessageListToolbar({ mailMessages, onActionSuccess }) {
	const { rpcService } = useRPCContext();
	const [loading, setLoading] = useState(false);
	const isSelectingMessages = useMailStore((state) => state.isSelectingMessages);
	const setIsSelectingMessages = useMailStore((state) => state.setIsSelectingMessages);
	const selectedMessagesIds = useMailStore((state) => state.selectedMessagesIds);
	const addSelectedMessage = useMailStore((state) => state.addSelectedMessage);
	const resetSelectedMessages = useMailStore((state) => state.resetSelectedMessages);
	const messageSearchValue = useMailStore((state) => state.messageSearchValue);
	const setMessageSearchValue = useMailStore((state) => state.setMessageSearchValue);
	const folder = useMailStore((state) => state.folder);
	const amountOfSelectedMessages = useMailStore((state) => state.amountOfSelectedMessages);
	const [searchValue, debouncedSearchValue, setDebouncedSearchValue] = useStateDebounced(messageSearchValue);
	useEffect(() => {
		setMessageSearchValue(debouncedSearchValue);
	}, [debouncedSearchValue]);
	useEffect(() => {
		if (!isSelectingMessages) resetSelectedMessages();
	}, [isSelectingMessages]);
	useEffect(() => {
		if (isSelectingMessages) setIsSelectingMessages(false);
	}, [folder]);
	const areAllMessagesSelected = useMemo(() => {
		if (!mailMessages) return false;
		return mailMessages.length <= amountOfSelectedMessages;
	}, [mailMessages, amountOfSelectedMessages]);
	const handleDeleteMessages = async () => {
		try {
			setLoading(true);
			await rpcService.Mail.DeleteMessages.v1.call({ mailMessageIds: Object.keys(selectedMessagesIds) });
			resetSelectedMessages();
			setIsSelectingMessages(false);
			onActionSuccess?.();
		} catch (err) {
			console.log("Mail.DeleteMessage.v1.call err", err);
			Alerts.error("There was a problem deleting the message", err.message);
		} finally {
			setLoading(false);
		}
	};
	const handleMarkMessagesUnread = async () => {
		try {
			setLoading(true);
			const allMessagesRead = mailMessages?.every((mailMessage) => {
				if (!selectedMessagesIds[mailMessage.id]) return true;
				return mailMessage.userRead;
			});
			await rpcService.Mail.SetMessageRead.v1.call({
				mailMessageIds: Object.keys(selectedMessagesIds),
				read: !allMessagesRead
			});
			resetSelectedMessages();
			setIsSelectingMessages(false);
			onActionSuccess?.();
		} catch (err) {
			console.log("Mail.SetMessageRead.v1.call err", err);
			Alerts.error("There was a problem marking the messages read/unread", err.message);
		} finally {
			setLoading(false);
		}
	};
	const handleMarkMessagesFlagged = async () => {
		try {
			setLoading(true);
			const allMessagesFlagged = mailMessages?.every((mailMessage) => {
				if (!selectedMessagesIds[mailMessage.id]) return true;
				return mailMessage.userFlagged;
			});
			await rpcService.Mail.SetMessageFlagged.v1.call({
				flagged: !allMessagesFlagged,
				mailMessageIds: Object.keys(selectedMessagesIds)
			});
			resetSelectedMessages();
			setIsSelectingMessages(false);
			onActionSuccess?.();
		} catch (err) {
			console.log("Mail.SetMessageRead.v1.call err", err);
			Alerts.error("There was a problem marking the messages read/unread", err.message);
		} finally {
			setLoading(false);
		}
	};
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsxs("div", {
		className: "space-x-xs flex",
		children: [/* @__PURE__ */ jsx("div", {
			className: "grow",
			children: /* @__PURE__ */ jsx(Text, {
				onChangeValue: setDebouncedSearchValue,
				placeholder: "Search...",
				search: true,
				value: searchValue
			})
		}), folder !== MAIL_VIEWS.TRASH && /* @__PURE__ */ jsxs(ButtonGroup, { children: [mailMessages && isSelectingMessages && /* @__PURE__ */ jsx(Button, {
			disabled: loading,
			onClick: () => {
				if (!areAllMessagesSelected) mailMessages.forEach((mailMessage) => {
					if (!selectedMessagesIds[mailMessage.id]) addSelectedMessage(mailMessage.id);
				});
				else resetSelectedMessages();
			},
			subtle: true,
			variant: areAllMessagesSelected ? "main" : "neutral",
			children: /* @__PURE__ */ jsx(Tooltip, {
				tooltip: areAllMessagesSelected ? "Deselect All" : "Select All",
				children: /* @__PURE__ */ jsx(Icon, { Icon: MdChecklist })
			})
		}), /* @__PURE__ */ jsx(Button, {
			className: "transition",
			disabled: loading,
			onClick: () => setIsSelectingMessages(!isSelectingMessages),
			subtle: true,
			variant: isSelectingMessages ? "main" : "neutral",
			children: /* @__PURE__ */ jsx(Tooltip, {
				tooltip: "Select Mode",
				children: /* @__PURE__ */ jsx(Icon, { Icon: isSelectingMessages ? IoCheckbox : MdCheckBoxOutlineBlank })
			})
		})] })]
	}), isSelectingMessages && /* @__PURE__ */ jsxs("div", {
		className: "mt-xs flex items-center justify-between",
		children: [/* @__PURE__ */ jsxs("span", {
			className: "text-color-neutral text-sm",
			children: [
				/* @__PURE__ */ jsx("span", { children: amountOfSelectedMessages || "No" }),
				" ",
				pluralize("Message", amountOfSelectedMessages),
				" Selected"
			]
		}), !!amountOfSelectedMessages && /* @__PURE__ */ jsxs(ButtonGroup, {
			size: "sm",
			subtle: true,
			children: [
				/* @__PURE__ */ jsxs(Button, {
					disabled: loading,
					onClick: handleMarkMessagesUnread,
					variant: "main",
					children: [
						/* @__PURE__ */ jsx(Icon, {
							Icon: IoMailUnread,
							size: "sm"
						}),
						" ",
						/* @__PURE__ */ jsx("span", { children: "Read" })
					]
				}),
				/* @__PURE__ */ jsxs(Button, {
					disabled: loading,
					onClick: handleMarkMessagesFlagged,
					variant: "caution",
					children: [
						/* @__PURE__ */ jsx(Icon, {
							Icon: HiFlag,
							size: "sm"
						}),
						" ",
						/* @__PURE__ */ jsx("span", { children: "Flag" })
					]
				}),
				/* @__PURE__ */ jsxs(Button, {
					disabled: loading,
					onClick: handleDeleteMessages,
					variant: "critical",
					children: [
						/* @__PURE__ */ jsx(Icon, {
							Icon: MdDeleteForever,
							size: "sm"
						}),
						" ",
						/* @__PURE__ */ jsx("span", { children: "Delete" })
					]
				})
			]
		})]
	})] });
}
//#endregion
export { MailMessageListToolbar };

//# sourceMappingURL=MailMessageListToolbar.js.map